import type { CityConfig } from "../city-template/types";

export const riversideConfig: CityConfig = {
  city: "Riverside",
  state: "California",
  stateAbbr: "CA",
  slug: "riverside",
  route: "/cities/riverside",
  title: "Emergency Passport Riverside, CA | Fast Passport Renewal Services",
  metaDescription: "Emergency passport and expedited passport services in Riverside, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Inland Empire travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Riverside, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport, fast passport renewal, or urgent passport help in Riverside? We help Inland Empire travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Riverside%20California%20cityscape%20at%20golden%20hour%20with%20historic%20Mission%20Inn%20tower%20and%20modern%20downtown%20buildings%2C%20palm%20trees%20and%20orange%20groves%2C%20warm%20sunset%20light%20over%20the%20Inland%20Empire%2C%20professional%20architectural%20photography%20with%20warm%20amber%20tones%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20and%20modern%20Southern%20California%20city%20environment&width=1800&height=700&seq=riverside-hero-001&orientation=landscape",
  heroImageAlt: "Riverside California skyline at golden hour with historic Mission Inn and downtown",
  geoPosition: "33.9533;-117.3962",
  geoPlacename: "Riverside, California",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "300 N Los Angeles St, Los Angeles, CA 90012",
    distance: "Located in downtown LA — approximately 55–70 minutes from Riverside via I-10",
  },
  nearbyCitiesZips: [
    { name: "Riverside", zips: "92501–92509, 92513–92519, 92521" },
    { name: "Moreno Valley", zips: "92551–92557" },
    { name: "Corona", zips: "92877–92883" },
    { name: "Jurupa Valley", zips: "91752, 92509" },
    { name: "Norco", zips: "92860" },
    { name: "Perris", zips: "92570–92572" },
    { name: "Hemet", zips: "92543–92546" },
    { name: "San Jacinto", zips: "92581–92583" },
    { name: "Menifee", zips: "92584, 92585, 92586" },
    { name: "Murrieta", zips: "92562, 92563, 92564" },
    { name: "Temecula", zips: "92589–92593" },
    { name: "Lake Elsinore", zips: "92530, 92531, 92532" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Inland Empire departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Los Angeles", route: "/cities/los-angeles" },
    { name: "Fast passport renewal in San Diego", route: "/cities/san-diego" },
    { name: "Emergency passport help in Orange County", route: "/cities/irvine" },
    { name: "Urgent passport services in Long Beach", route: "/cities/long-beach" },
    { name: "Same-day passport help in Anaheim", route: "/cities/anaheim" },
    { name: "Passport courier services near Santa Ana", route: "/cities/santa-ana" },
    { name: "Rush passport help in Pasadena", route: "/cities/pasadena" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Riverside remotely via secure courier. We do not have a walk-in office in Riverside — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Inland Empire travelers.",
  fpcOfficeDistance: "Serving all of Riverside County",
  serviceAreaIntro: "From downtown Riverside to the Mission Inn District, from Canyon Crest to Woodcrest — Fast Passport Center serves the entire Riverside community. We regularly help travelers from Moreno Valley, Corona, Hemet, and Temecula get their passports fast.",
  nearbyCitiesIntro: "We serve all Riverside neighborhoods and nearby Inland Empire cities including Moreno Valley, Corona, Jurupa Valley, Perris, Hemet, and Temecula. If you are in Riverside County, we have you covered.",
  howItWorksIntro: "Most Riverside travelers complete everything online and ship documents overnight. No need to drive to Los Angeles or wait in line at the regional agency.",
  mistakesIntro: "With Ontario International Airport (ONT) serving as the Inland Empire's primary gateway, Riverside travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Riverside and Inland Empire travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about emergency passports, expedited passports, or fast passport renewal in Riverside? Here are the most common ones we hear from Inland Empire travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Inland Empire international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Daniel M.",
      location: "Riverside, CA",
      rating: 5,
      text: "My flight to Cancun was in 3 days and my passport was expired. Fast Passport Center got me a new one in under 48 hours. Saved our family vacation completely.",
      service: "Emergency Passport",
    },
    {
      name: "Lisa K.",
      location: "Moreno Valley, CA",
      rating: 5,
      text: "Needed to renew for a business trip to Tokyo. The process was incredibly smooth, they reviewed my documents, and I had my passport in 4 days. Highly recommend for Inland Empire residents.",
      service: "Passport Renewal",
    },
    {
      name: "Chris P.",
      location: "Corona, CA",
      rating: 5,
      text: "Lost my passport a week before my honeymoon to Italy. They walked me through the DS-64 process and got me a replacement in 36 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Patricia W.",
      location: "Temecula, CA",
      rating: 5,
      text: "As a frequent international traveler from the Inland Empire, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable every time.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "riverside-1",
      name: "Riverside Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "4150 Chicago Ave",
      city: "Riverside",
      state: "CA",
      zip: "92507",
      phone: "(951) 276-6104",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.2 miles from UC Riverside",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "riverside-2",
      name: "Moreno Valley Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "23580 Sunnymead Ranch Pkwy",
      city: "Moreno Valley",
      state: "CA",
      zip: "92557",
      phone: "(951) 924-8310",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.8 miles from TownGate Shopping Center",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for eastern Riverside County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "riverside-3",
      name: "Corona Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1211 S Main St",
      city: "Corona",
      state: "CA",
      zip: "92882",
      phone: "(951) 736-4134",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from Corona City Hall",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "riverside-4",
      name: "Hemet Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "439 N State St",
      city: "Hemet",
      state: "CA",
      zip: "92543",
      phone: "(951) 658-6617",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Hemet",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for San Jacinto Valley residents. Call to confirm passport hours.",
      icon: "ri-mail-line",
    },
    {
      id: "riverside-5",
      name: "Temecula Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "28473 Rancho California Rd",
      city: "Temecula",
      state: "CA",
      zip: "92590",
      phone: "(951) 694-1566",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.5 miles from Old Town Temecula",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for southern Riverside County residents. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "riverside-6",
      name: "Riverside County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "2720 Gateway Dr",
      city: "Riverside",
      state: "CA",
      zip: "92507",
      phone: "(951) 486-7000",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.5 miles from the Riverside County Administration Center",
      website: "https://rivco.org/",
      websiteLabel: "Riverside County Clerk",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown Los Angeles", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Inland Empire photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Riverside County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Riverside applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Inland Empire families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Riverside County travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Riverside." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Riverside address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Riverside before international travel?",
      a: "Yes. Fast Passport Center provides emergency passport services in Riverside for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most Inland Empire travelers.",
    },
    {
      q: "How fast can I renew my passport in Riverside?",
      a: "Fast Passport Center offers fast passport renewal in Riverside in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent.",
    },
    {
      q: "What if my passport expires before my cruise?",
      a: "If your passport expires before your cruise, contact Fast Passport Center immediately. Many cruise lines require your passport to be valid for at least 6 months beyond your travel date. We can expedite your passport renewal in Riverside in 24–48 hours so you don't miss your departure.",
    },
    {
      q: "Can I get a same-day passport appointment near Riverside?",
      a: "Same-day passport appointments are only available at the LA Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to Riverside County residents.",
    },
    {
      q: "Can I expedite a child passport in Riverside?",
      a: "Yes. Fast Passport Center expedites child passports in Riverside for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Inland Empire families through the entire process.",
    },
    {
      q: "What should I do if I lost my passport before a trip?",
      a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Riverside with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours.",
    },
    {
      q: "How fast can I get a passport in Riverside?",
      a: "With Fast Passport Center's emergency courier service, Riverside travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the Los Angeles Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Riverside?",
      a: "Popular options include the Riverside Main Post Office (92507), Moreno Valley Post Office (92557), Corona Post Office (92882), and Riverside County Clerk (92507). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Riverside?",
      a: "Same-day passports are only available at the LA Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in Moreno Valley, Corona, or Temecula?",
      a: "We serve all of Riverside County including Moreno Valley, Corona, Hemet, Temecula, Murrieta, Perris, and Lake Elsinore. Our overnight shipping labels work from any UPS location, so your location within the county doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Riverside?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Riverside?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Riverside, Moreno Valley, and Corona offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Riverside?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Riverside?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Riverside County families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Riverside", address: "3520 Central Ave, Riverside, CA 92506", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Moreno Valley", address: "12125 Day St, Moreno Valley, CA 92557", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Corona", address: "420 N Main St, Corona, CA 92880", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Temecula", address: "41789 Nicole Ln, Temecula, CA 92591", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Hemet", address: "1860 W Florida Ave, Hemet, CA 92543", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Riverside", address: "3890 Chicago Ave, Riverside, CA 92507", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Corona", address: "400 E Rincon St, Corona, CA 92879", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Riverside", address: "1270 E Ontario Ave, Corona, CA 92881", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Riverside",
      address: "6425 Market St, Riverside, CA 92506",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Moreno Valley",
      address: "22735 Alessandro Blvd, Moreno Valley, CA 92553",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Corona",
      address: "1101 E 6th St, Corona, CA 92879",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Temecula",
      address: "29099 Rancho California Rd, Temecula, CA 92591",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for southern Riverside County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "ONT",
      name: "Ontario International Airport",
      distance: "20 miles from downtown Riverside",
      address: "1923 E Avion St, Ontario, CA 91761",
      note: "The primary airport for the Inland Empire. International flights to Mexico and domestic connections worldwide.",
      flightUrl: "https://www.flyontario.com/",
      flightLabel: "Ontario Airport",
      featured: true,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "60 miles from Riverside",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Major international hub for long-haul flights to Asia, Europe, and South America. Accessible via I-10.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: false,
    },
    {
      code: "SNA",
      name: "John Wayne Airport (Orange County)",
      distance: "35 miles from Riverside",
      address: "18601 Airport Way, Santa Ana, CA 92707",
      note: "Convenient for western Riverside County. International flights to Mexico, Canada, and connections.",
      flightUrl: "https://www.ocair.com/",
      flightLabel: "John Wayne Airport",
      featured: false,
    },
  ],
};